/**
 * 预筛选通知服务
 * 负责将后端 reason 映射到用户友好的错误分类和消息
 * 基于"最小打扰"设计原则
 *
 * @see Requirements 3.1, 3.2, 3.3, 3.4, 3.5, 4.1, 4.2
 */

import {
  PrescreenErrorCategory,
  PrescreenErrorMessage,
} from "@/types/prescreen";
import { PrescreenResponse } from "@/services/assessmentService";

/**
 * 后端 reason 到错误分类的映射表
 */
const REASON_TO_CATEGORY_MAP: Record<string, PrescreenErrorCategory | null> = {
  // 阻断性错误 - 必须提示
  not_fish: PrescreenErrorCategory.NOT_FISH,
  blurry: PrescreenErrorCategory.BLURRY,
  artificial: PrescreenErrorCategory.ARTIFICIAL,
  too_dark: PrescreenErrorCategory.LIGHTING,
  too_bright: PrescreenErrorCategory.LIGHTING,
  file_too_large: PrescreenErrorCategory.FILE_TOO_LARGE,

  // 技术错误 - 合并为通用消息
  invalid_image: PrescreenErrorCategory.INVALID_FORMAT,
  invalid_magic_bytes: PrescreenErrorCategory.INVALID_FORMAT,
  decode_failed: PrescreenErrorCategory.INVALID_FORMAT,
  ssrf_blocked: PrescreenErrorCategory.LOAD_FAILED,
  download_failed: PrescreenErrorCategory.LOAD_FAILED,
  dns_rebinding: PrescreenErrorCategory.LOAD_FAILED,

  // 静默处理 - 返回 null（Fail Open 策略）
  service_unavailable: null,
  timeout: null,
  unknown: null,
};

/**
 * 所有已知的阻断性错误 reason 列表
 */
export const BLOCKING_REASONS = [
  "not_fish",
  "blurry",
  "artificial",
  "too_dark",
  "too_bright",
  "file_too_large",
  "invalid_image",
  "invalid_magic_bytes",
  "decode_failed",
  "ssrf_blocked",
  "download_failed",
  "dns_rebinding",
];

/**
 * 静默处理的 reason 列表
 */
export const SILENT_REASONS = ["service_unavailable", "timeout", "unknown"];

/**
 * 将后端 reason 映射到错误分类
 * @param reason 后端返回的原始 reason 字符串
 * @returns 错误分类，如果应该静默处理则返回 null
 */
export function mapReasonToCategory(
  reason: string
): PrescreenErrorCategory | null {
  if (!reason) {
    return null;
  }

  const normalizedReason = reason.toLowerCase().trim();

  if (normalizedReason in REASON_TO_CATEGORY_MAP) {
    return REASON_TO_CATEGORY_MAP[normalizedReason];
  }

  // 未知 reason 静默处理（Fail Open）
  console.warn(
    `[PrescreenNotification] Unknown reason: ${reason}, applying silent fallback`
  );
  return null;
}

/**
 * 判断错误是否可以通过重试解决
 * @param category 错误分类
 * @returns true 表示可以重试，false 表示需要重新选择图片
 */
export function canRetry(category: PrescreenErrorCategory): boolean {
  switch (category) {
    // 这些错误重试同一张图没有意义，需要重新选择
    case PrescreenErrorCategory.FILE_TOO_LARGE:
    case PrescreenErrorCategory.INVALID_FORMAT:
    case PrescreenErrorCategory.NOT_FISH:
    case PrescreenErrorCategory.ARTIFICIAL:
      return false;
    // 这些错误可能是临时的，可以重试
    case PrescreenErrorCategory.LOAD_FAILED:
    case PrescreenErrorCategory.BLURRY:
    case PrescreenErrorCategory.LIGHTING:
      return true;
    default:
      return false;
  }
}

/**
 * 判断是否应该显示内联反馈
 * @param response 预筛选响应
 * @returns true 表示需要显示内联反馈，false 表示静默处理
 */
export function shouldShowFeedback(response: PrescreenResponse): boolean {
  // 成功响应 - 静默处理
  if (response.passed) {
    return false;
  }

  // 失败响应 - 检查 reason 是否需要显示
  const category = mapReasonToCategory(response.reason);
  return category !== null;
}

/**
 * 获取本地化的错误消息
 * @param category 错误分类
 * @param t i18n 翻译函数
 * @returns 包含标题、描述和操作提示的消息对象
 */
export function getLocalizedMessage(
  category: PrescreenErrorCategory,
  t: (key: string, options?: Record<string, unknown>) => string
): PrescreenErrorMessage {
  const baseKey = `prescreen.errors.${category}`;

  return {
    title: t(`${baseKey}.title`),
    description: t(`${baseKey}.description`, { maxSize: 10 }),
    actionHint: t(`${baseKey}.actionHint`),
    iconType: getIconType(category),
    canRetry: canRetry(category),
  };
}

/**
 * 根据错误分类获取图标类型
 */
function getIconType(
  category: PrescreenErrorCategory
): "warning" | "error" | "info" {
  switch (category) {
    case PrescreenErrorCategory.NOT_FISH:
    case PrescreenErrorCategory.ARTIFICIAL:
      return "error";
    case PrescreenErrorCategory.BLURRY:
    case PrescreenErrorCategory.LIGHTING:
      return "warning";
    case PrescreenErrorCategory.FILE_TOO_LARGE:
    case PrescreenErrorCategory.INVALID_FORMAT:
    case PrescreenErrorCategory.LOAD_FAILED:
      return "info";
    default:
      return "warning";
  }
}

/**
 * 检查是否为 Soft Pass（低置信度但放行）
 * @param response 预筛选响应
 * @param threshold 置信度阈值，低于此值视为 soft pass
 * @returns true 表示是 soft pass
 */
export function isSoftPass(
  response: PrescreenResponse,
  threshold = 0.7
): boolean {
  if (!response.passed) {
    return false;
  }

  const confidence = response.confidence;
  return confidence !== null && confidence < threshold;
}

/**
 * 获取 Soft Pass 的提示消息
 * @param t i18n 翻译函数
 * @returns 提示消息
 */
export function getSoftPassMessage(t: (key: string) => string): string {
  return t("prescreen.softPass.tooltip");
}
